<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useJobsStore } from '@/stores/jobs'
import { useNotificationsStore } from '@/stores/notifications'
import { usePhoneHealthStore } from '@/stores/phoneHealth'
import { useShoppingStore } from '@/stores/shopping'
import { useCheckinStore } from '@/stores/checkin'
import AppFrame from '@/components/app/AppFrame.vue'
import { useSignOutRedirect } from '@/lib/sign-out'

const auth = useAuthStore()
const jobs = useJobsStore()
const notifications = useNotificationsStore()
const phoneHealth = usePhoneHealthStore()
const shopping = useShoppingStore()
const checkin = useCheckinStore()
const route = useRoute()
useSignOutRedirect()

/**
 * `route.name` is undefined until the first navigation resolves, so without
 * that guard a hard load of a public page renders the signed-in frame for a
 * tick and its calls 401.
 */
const bare = computed(() => !route.name || route.meta.public === true || route.meta.bare === true || !auth.isSignedIn)

// Job progress and the inbox both arrive over socket.io for as long as
// someone is signed in. Signing out drops both, so the next person to use
// this browser does not inherit the last one's notifications.
watch(
  () => auth.accessToken,
  (token) => {
    if (token) {
      jobs.connect(token)
      notifications.connect(token)
    } else {
      jobs.disconnect()
      notifications.disconnect()
    }
  },
  { immediate: true },
)

// The phone's own health store is read when the app is opened or comes back
// to the front (M-72), once a person is signed in; a browser has none.
watch(
  () => auth.me?.memberId ?? null,
  (memberId) => {
    if (memberId && phoneHealth.native) void phoneHealth.init()
    // An update or a force stop drops the reminder's alarm; put it back.
    if (memberId) void checkin.ensureReminder()
  },
  { immediate: true },
)
function onForeground() {
  if (document.visibilityState !== 'visible') return
  void reconnect()
  if (auth.isSignedIn) {
    void phoneHealth.autoSync()
    void checkin.ensureReminder()
  }
}

/**
 * Back in signal (N-06): finish signing in if the app was opened without
 * any, then send what was changed on the shopping list meanwhile.
 */
async function reconnect() {
  if (!auth.isSignedIn && auth.unreachable && !(await auth.restore())) return
  if (auth.isSignedIn && (shopping.offline || shopping.queue.length > 0)) await shopping.flush()
}
function onOnline() {
  void reconnect()
}
onMounted(() => {
  document.addEventListener('visibilitychange', onForeground)
  window.addEventListener('online', onOnline)
})
onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', onForeground)
  window.removeEventListener('online', onOnline)
})
</script>

<template>
  <main v-if="bare" class="h-full overflow-y-auto px-4 py-4" style="padding-top: max(1rem, env(safe-area-inset-top))">
    <RouterView />
  </main>
  <AppFrame v-else>
    <RouterView />
  </AppFrame>
</template>
